import { Fragment, useEffect, type ReactNode } from 'react'
import { C } from '../components/shared'
import privacyMd from '../legal/privacy.md?raw'
import termsMd from '../legal/terms.md?raw'

/*
 * /privacy and /terms (L20, 2026-10-06). The text is src/legal/privacy.md and src/legal/terms.md, copied from
 * the SerpDesk app repo's docs/legal/PRIVACY_POLICY.md and TERMS_OF_SERVICE.md (branch fix/live-l20) without
 * their source notes. Edit the documents there and copy them here; this page only renders them.
 *
 * The renderer covers what the two documents use: # to ### headings, paragraphs, - and 1. lists, pipe tables,
 * **bold**, `code` and [links](url).
 */

const DOCS = {
  privacy: { md: privacyMd, title: 'Privacy Policy' },
  terms: { md: termsMd, title: 'Terms of Service' },
} as const

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g

function inline(text: string): ReactNode[] {
  return text.split(INLINE).filter(Boolean).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} style={{ color: C.ink }}>{part.slice(2, -2)}</strong>
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i} style={{ fontFamily: 'var(--mono)', fontSize: '.88em', background: C.ghost, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px' }}>{part.slice(1, -1)}</code>
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/)
    if (link) {
      const external = /^https?:/.test(link[2])
      return <a key={i} href={link[2]} style={{ color: C.purple }} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{link[1]}</a>
    }
    return <Fragment key={i}>{part}</Fragment>
  })
}

const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())

function render(md: string): { title: string; blocks: ReactNode[] } {
  const lines = md.replace(/<!--[\s\S]*?-->/g, '').split(/\r?\n/)
  const blocks: ReactNode[] = []
  let title = ''
  let i = 0
  const p = { fontSize: 15, color: C.body, lineHeight: 1.75, margin: '0 0 16px' }
  while (i < lines.length) {
    const line = lines[i]
    if (!line.trim()) { i++; continue }
    if (line.startsWith('# ')) { title = line.slice(2).trim(); i++; continue }
    if (line.startsWith('## ')) {
      blocks.push(<h2 key={i} style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 700, color: C.ink, margin: '40px 0 14px' }}>{inline(line.slice(3))}</h2>)
      i++; continue
    }
    if (line.startsWith('### ')) {
      blocks.push(<h3 key={i} style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 700, color: C.ink, margin: '28px 0 10px' }}>{inline(line.slice(4))}</h3>)
      i++; continue
    }
    if (line.trim().startsWith('|')) {
      const rows: string[][] = []
      const start = i
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[i])) rows.push(cells(lines[i]))
        i++
      }
      const [head, ...body] = rows
      blocks.push(
        <div key={start} style={{ overflowX: 'auto', margin: '0 0 20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, color: C.body }}>
            <thead><tr>{head.map((c, k) => <th key={k} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${C.border}`, color: C.ink, fontWeight: 700 }}>{inline(c)}</th>)}</tr></thead>
            <tbody>{body.map((r, j) => <tr key={j}>{r.map((c, k) => <td key={k} style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, verticalAlign: 'top', lineHeight: 1.55 }}>{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      )
      continue
    }
    const bullet = /^- /
    const numbered = /^\d+\. /
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = numbered.test(line)
      const items: string[] = []
      const start = i
      while (i < lines.length && (ordered ? numbered : bullet).test(lines[i])) {
        items.push(lines[i].replace(ordered ? numbered : bullet, ''))
        i++
      }
      const List = ordered ? 'ol' : 'ul'
      blocks.push(<List key={start} style={{ ...p, paddingLeft: 22 }}>{items.map((t, k) => <li key={k} style={{ marginBottom: 6 }}>{inline(t)}</li>)}</List>)
      continue
    }
    const para: string[] = []
    const start = i
    while (i < lines.length && lines[i].trim() && !/^(#{1,3} |- |\d+\. |\s*\|)/.test(lines[i])) {
      para.push(lines[i])
      i++
    }
    blocks.push(<p key={start} style={p}>{para.map((t, k) => <Fragment key={k}>{k > 0 && <br />}{inline(t)}</Fragment>)}</p>)
  }
  return { title, blocks }
}

export default function LegalPage({ doc }: { doc: keyof typeof DOCS }) {
  const { title, blocks } = render(DOCS[doc].md)
  useEffect(() => { document.title = `${DOCS[doc].title} | SerpDesk` }, [doc])
  return (
    <>
      <section style={{ background: C.hero, color: '#fff', paddingTop: 125, paddingBottom: 56 }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px' }}>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 'clamp(30px, 5vw, 44px)', fontWeight: 700, margin: 0 }}>{title || DOCS[doc].title}</h1>
        </div>
      </section>
      <section style={{ background: C.surface, padding: '40px 0 80px' }}>
        <article style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px' }}>{blocks}</article>
      </section>
    </>
  )
}
